import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   * Shows trash icon instead of minus when quantity is at minimum (1)
   */
  updateButtonStates() {
    const { minusButton, plusButton, minusIcon, trashIcon } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Never disable minus button in cart - show trash icon at min quantity instead
    minusButton.disabled = false;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;

    // Toggle between minus icon and trash icon
    if (minusIcon && trashIcon) {
      if (value <= min) {
        minusIcon.style.display = 'none';
        trashIcon.style.display = 'inline-flex';
      } else {
        minusIcon.style.display = 'inline-flex';
        trashIcon.style.display = 'none';
      }
    }
  }

  /**
   * Handles the quantity decrease event.
   * When quantity is at minimum, triggers item removal instead of decrease.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { min, value } = this.getCurrentValues();

    if (value <= min) {
      // At minimum quantity - remove the item by setting quantity to 0
      const { quantityInput } = this.refs;
      quantityInput.value = '0';
      this.dispatchEvent(
        new QuantitySelectorUpdateEvent(0, Number(quantityInput.dataset.cartLine) || undefined)
      );
      return;
    }

    this.updateQuantity(-1);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
